import React, { useEffect, useState } from "react";
import axios from "axios";
import BannerFormModal from "./BannerFormModal";
import ConfirmDeleteModal from "./ConfirmDeleteModal";
import { Icon } from "organisms/iconCommonent";
import { MdDelete } from "react-icons/md";
import { FaRegEdit } from "react-icons/fa";

interface Pattners {
  _id: string;
  image: string;
  heading: string;
  subheading: string;
  description: string;
}

interface PatternsProps {
  sectionId: string;
}

export default function PattnersDashboard({ sectionId }: PatternsProps) {
  const [banner, setBanner] = useState<Pattners | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [editingBanner, setEditingBanner] = useState<Pattners | null>(null);

  const fetchBanners = async () => {
    try {
      const res = await axios.get(
        `${process.env.REACT_APP_LOCAL_URL}/api/common-layout/${sectionId}`
      );
      // Only pick the 2nd banner
      const selectedBanner = res.data.data;
      setBanner(selectedBanner);
    } catch (error) {
      console.error("Error fetching banner:", error);
    }
  };

  useEffect(() => {
    fetchBanners();
  }, []);

  const handleEdit = (banner: Pattners) => {
    setEditingBanner(banner);
    setShowForm(true);
  };

  return (
    <div className="pt-8">
      <div className="flex justify-between items-center mb-6">
        <h2 className="font-lato-regular ">Section - 05</h2>
        <hr />
      </div>

      {/* Banner Box */}
      {banner && (
        <div className="width-[18rem] mx-auto bg-white border border-gray-200 rounded-xl  overflow-hidden ">
          <img
            src={`${process.env.REACT_APP_LOCAL_URL}/${banner.image}`}
            alt={banner.heading}
            className="w-full h-[18rem] object-cover"
          />
          <div className="p-5">
            <h2 className="text-xl font-bold  text-black font-lato-regular mb-2">
              {banner.heading}
            </h2>
            <h6 className=" text-md text-black font-lato-regular mb-4">
              {banner.description}
            </h6>

            <div className="flex justify-start gap-3">
              <button
                className="bg-blue-500 font-lato-regular text-white px-4 py-2 rounded"
                onClick={() => handleEdit(banner)}
              >
                <span className="inline-block mr-1">
                  <Icon icon={FaRegEdit} />
                </span>{" "}
                Edit
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Form Modal */}
      {showForm && (
        <PattnersFormModal
          onClose={() => setShowForm(false)}
          fetchBanners={fetchBanners}
          editingBanner={editingBanner}
        />
      )}
    </div>
  );
}

interface PattnersFormModalProps {
  onClose: () => void;
  fetchBanners: () => void;
  editingBanner: Pattners | null;
}

const PattnersFormModal = ({
  onClose,
  fetchBanners,
  editingBanner,
}: PattnersFormModalProps) => {
  const [heading, setHeading] = useState(editingBanner?.heading || "");
  const [description, setDescription] = useState(
    editingBanner?.description || ""
  );
  const [files, setFiles] = useState<File[]>([]);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) setFiles(Array.from(e.target.files));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const formData = new FormData();
    formData.append("heading", heading);
    formData.append("description", description);

    // append all files with correct field name
    files.forEach((file) => formData.append("image", file));

    try {
      if (editingBanner) {
        await axios.patch(
          `${process.env.REACT_APP_LOCAL_URL}/api/common-layout/${editingBanner._id}`,
          formData,
          { headers: { "Content-Type": "multipart/form-data" } }
        );
      } else {
        await axios.post(`${process.env.REACT_APP_LOCAL_URL}/api/common-layout`, formData, {
          headers: { "Content-Type": "multipart/form-data" },
        });
      }

      fetchBanners();
      onClose();
    } catch (error) {
      console.error("Error saving banner:", error);
    }
  };

  return (
    <div className="fixed inset-0 flex justify-center items-center bg-black/40 z-50">
      <div className="bg-white p-6 rounded-xl w-[500px] shadow-lg">
        <form onSubmit={handleSubmit} className="space-y-4">
          <label className="block font-lato-bold">Heading</label>
          <input
            type="text"
            value={heading}
            onChange={(e) => setHeading(e.target.value)}
            required
            className="w-full border border-gray-300 rounded-md p-2"
          />

          <label className="block font-lato-bold">Description</label>
          <textarea
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            required
            className="w-full border border-gray-300 rounded-md p-2 h-24"
          />

          <label className="block font-lato-bold">Upload Images</label>

          {editingBanner?.image && (
            <div className="flex gap-2 overflow-x-auto mb-2">
              <img
                src={`${process.env.REACT_APP_LOCAL_URL}/${editingBanner.image}`}
                alt="Current"
                className="w-24 h-24 object-cover rounded-md border"
              />
            </div>
          )}

          <input
            type="file"
            multiple
            accept="image/*"
            onChange={handleFileChange}
            className="w-full border border-gray-300 rounded-md p-2"
          />

          <div className="flex justify-end gap-3 mt-4">
            <button
              type="submit"
              className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
            >
              {editingBanner ? "Update" : "Upload"}
            </button>
            <button
              type="button"
              onClick={onClose}
              className="text-red-500 border border-red-500 px-4 py-2 rounded-md hover:bg-red-50"
            >
              Cancel
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};
